import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
} from '@angular/core';
import { ListItem } from '../../../models/api/core/list/list-item';
import {
  FilterBase,
  SystemFilter,
  UserFilter,
} from '../../../models/api/reports/all-filters-response';
import { FilterDetail } from '../../../models/api/reports/filter-detail-response';
import { Action } from '../../../models/core/action';
import { FiltersService } from '../../../services/filters/filters.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { IconButtonComponent } from '../../core/buttons/icon-button.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { ToolbarButtonComponent } from '../../core/buttons/toolbar-button.component';
import { RenameDialogComponent } from '../../core/dialogs/rename-dialog.component';
import { TagComponent } from '../../core/labels/tag.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { CardComponent } from '../../core/panels/card.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { FilterPrefDialogComponent } from '../core/filter-pref-dialog.component';
import { FilterRefViewComponent } from '../core/filter-ref-view.component';
import { FilterViewComponent } from '../core/filter-view.component';
import { ShareDialogComponent } from '../core/share-dialog.component';
import { FormatPipe } from '../../../pipes/format.pipe';

@Component({
  selector: 'app-my-filters',
  imports: [
    CommonModule,
    ActionRowComponent,
    ButtonComponent,
    TableComponent,
    ColumnComponent,
    ToolbarButtonComponent,
    ActionButtonComponent,
    MenuComponent,
    CardComponent,
    FilterViewComponent,
    IconButtonComponent,
    FilterPrefDialogComponent,
    LoadingPanelComponent,
    TagComponent,
    FilterRefViewComponent,
    ShareDialogComponent,
    RenameDialogComponent,
    NgTemplateOutlet,
    EmptyStateComponent,
    FormatPipe,
  ],
  template: `
    <app-action-row class="tw-w-full">
      <div *ngTemplateOutlet="headerTemplate" left1></div>
      <app-button
        right1
        label="New filter"
        routerLink="/reports/edit-filter"
        (click)="navigateNewFilter()"
      />
      <app-icon-button
        right2
        [border]="false"
        ariaLabel="Filter preferences"
        tooltip="Filter preferences"
        icon="fa-light fa-gear"
        (click)="showPreferences()"
      />
    </app-action-row>

    @if (showSearch && !searchClient()) {
      <app-empty-state
        label="Select client"
        message="Select a client to view filters"
        icon="fa-light fa-circle-info"
        height="300px"
      />
    } @else {
      <app-table
        [dataSource]="filters()"
        class="tw-w-full"
        [expandable]="true"
        [hideExpandAll]="true"
        recordLabel="filter"
        scrollHeight=""
        dataKey="filterId"
        sortField="updatedDate"
        [sortOrder]="-1"
        [showFilter]="false"
        [showFilterButton]="false"
        [stripedRows]="false"
        [loading]="loading()"
        (rowExpand)="onRowExpand($event)"
        [rowExpandLabel]="rowExpandLabel"
        [showPaginator]="showPaginator"
        [scrollHeight]="scrollHeight"
        [showRefresh]="true"
        (refresh)="refresh()"
        caption="Report filters"
      >
        <app-column
          name="filterName"
          classes="tw-font-bold"
          label="Name"
          width="25%"
          filterType="text"
          ><ng-template let-data
            ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
              {{ data.filterName }}
            </div>
          </ng-template>
        </app-column>
        <app-column
          name="contractNumber"
          label="Client"
          width="25%"
          filterType="select"
          filterLabelName="clientName"
        >
          <ng-template let-data>
            <div class="tw-max-w-full tw-break-words tw-whitespace-normal">
              @if (data.contractNumber && data.clientName) {
                {{ data.contractNumber }} - {{ data.clientName }}
              } @else if (showSearch) {
                @if (searchClient()) {
                  {{ searchClient()?.description }}
                }
              }
            </div>
          </ng-template>
        </app-column>
        <app-column
          name="owner"
          label="Created by"
          width="20%"
          filterType="select"
        >
          <ng-template let-data>
            <div class="tw-max-w-full tw-break-words tw-whitespace-normal">
              @if (isUserFilter(data)) {
                <app-tag type="Purple">{{ data.owner }}</app-tag>
              } @else {
                <app-tag type="Grey">system</app-tag>
              }
            </div>
          </ng-template>
        </app-column>
        <app-column
          name="updatedDate"
          label="Last updated"
          format="dt"
          width="20%"
          filterType="date"
          ><ng-template let-data
            ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
              {{ data.updatedDate | format: 'dt' }}
            </div>
          </ng-template>
        </app-column>
        @if (showSearch) {
          <app-column name="" label="Run filter" width="10%">
            <ng-template let-data>
              <div
                class="tw-flex tw-max-w-full tw-break-words tw-whitespace-normal"
              >
                <app-toolbar-button
                  type="Compact"
                  icon="fa-play fa-regular"
                  [ariaLabel]="'Run ' + data.filterName + ' filter'"
                  [disabled]="searching"
                  (click)="search.emit(data)"
                />
              </div>
            </ng-template>
          </app-column>
        }

        <app-column
          name="actions"
          label="Actions"
          headerClasses="tw-flex tw-justify-center"
          [frozen]="true"
          [sort]="false"
        >
          <ng-template let-data>
            <div class="tw-max-w-full tw-break-words tw-whitespace-normal">
              <app-action-button
                [ariaLabel]="'More actions for ' + data.filterName"
                [menus]="[userMenu, systemMenu]"
                [row]="data"
                (click)="
                  isUserFilter(data)
                    ? toggleUserMenu($event, data)
                    : systemMenu.toggle($event, data)
                "
              />
            </div>
          </ng-template>
        </app-column>
        <ng-template #expand let-data>
          <div class="tw-flex tw-gap-4">
            <app-card label="Filter" class="tw-basis-1/2">
              @if (filterDetails[data.filterId]) {
                <app-filter-view [dataSource]="filterDetails[data.filterId]" />
              } @else {
                <app-loading-panel
                  state="Loading"
                  loadingStyle="Box"
                  [loadingHeight]="200"
                />
              }
            </app-card>
            <app-card label="References" class="tw-basis-1/2">
              <app-filter-ref-view
                [dataSource]="filterDetails[data.filterId]"
              />
            </app-card>
          </div>
        </ng-template>
      </app-table>
      @if (showHelpText) {
        <div class="tw-pt-8 tw-italic tw-text-sm">
          Created filters will appear here. Use this tab to edit, copy, share or
          delete filters you no longer need. Create a new filter using New
          filter.
        </div>
      }

      <app-menu
        [items]="userActions"
        #userMenu
        (click)="onAction($event, userMenu.data)"
      />
      <app-menu
        [items]="systemActions"
        #systemMenu
        (click)="onAction($event, systemMenu.data)"
      />
      <app-share-dialog #shareDialog />
      <app-rename-dialog
        label="Rename filter"
        #renameDialog
        (rename)="renameFilter($event, userMenu.data)"
      />
    }
    <app-filter-pref-dialog #prefDialog />
  `,
  styles: [],
})
export class MyFiltersComponent extends BaseComponent implements OnInit {
  @ViewChild('prefDialog')
  prefDialog: FilterPrefDialogComponent;

  @ViewChild('shareDialog')
  shareDialog: ShareDialogComponent;

  @ViewChild('renameDialog')
  renameDialog: RenameDialogComponent;

  @ViewChild('userMenu')
  userMenu: MenuComponent;

  @Output()
  search = new EventEmitter<FilterBase>();

  @Input()
  searching = false;

  @Input()
  showSearch = false;

  searchClient = input<ListItem>(null);

  @Input()
  showHelpText = true;

  @Input()
  headerTemplate: TemplateRef<any>;

  @Input()
  showPaginator = true;

  @Input()
  scrollHeight: string;

  @Input()
  redirectTo = 'reports?tab=filters';

  isFilterOnly = false;

  private readonly snackbar = inject(Snackbar);
  private readonly filtersService = inject(FiltersService);

  public rowExpandLabel = (filter: FilterBase): string => {
    return `${filter.filterName} filter`;
  };

  filters = computed(() => {
    const client = this.searchClient();
    if (client && this.showSearch) {
      return [
        ...this.filtersService.systemFilters().map((f) => ({
          ...f,
          contractNumber: client.value,
        })),
        ...this.filtersService
          .userFilters()
          .filter((x) => x.contractNumber === client.value),
      ];
    } else {
      return [
        ...this.filtersService.systemFilters(),
        ...this.filtersService.userFilters(),
      ];
    }
  });

  readonly loading = this.filtersService.userFiltersLoading;

  filterDetails: Record<string, FilterDetail> = {};

  constructor() {
    super();
    this.isFilterOnly =
      !this.session.authorize('ReportsModule') &&
      this.session.authorize('FiltersModule');

    effect(() => {
      if (this.filtersService.needsRefresh()) {
        this.filtersService.needsRefresh.set(false);
        this.filtersService.loadAllFilters('JURIS', 'CLAIM', null, true);
      }
    });
  }

  async ngOnInit() {
    await this.filtersService.loadAllFilters('JURIS', 'CLAIM', null);
  }

  isUserFilter(filter: SystemFilter | UserFilter): filter is UserFilter {
    return !('description' in filter);
  }

  userActions: Action[] = [
    { label: 'Edit', key: 'edit' },
    { label: 'Copy', key: 'copy' },
    { label: 'Rename', key: 'rename' },
    { label: 'Share' },
    { label: 'Delete', key: 'delete' },
  ];

  systemActions: Action[] = [{ label: 'Copy', key: 'copy' }];

  toggleUserMenu(event: Event, filter: SystemFilter | UserFilter): void {
    this.userMenu.items = [
      { label: 'Edit', key: 'edit' },
      { label: 'Copy', key: 'copy' },
      { label: 'Rename', key: 'rename' },
      { label: 'Share' },
      { label: 'Delete', key: 'delete', disabled: filter.hasReferences },
    ];

    this.userMenu.toggle(event, filter);
  }

  async onAction(action: Action, filter: SystemFilter | UserFilter) {
    if (action.key === 'delete') {
      this.confirm.show({
        label: 'Delete filter',
        message: `Are you sure you want to delete ${filter.filterName}?`,
        width: '35rem',
        data: filter,
        confirm: async () => await this.executeDelete(filter),
      });
    } else if (action.key === 'edit') {
      this.router.navigate(['/reports/edit-filter', filter.filterId], {
        queryParams: {
          redirectTo: this.redirectTo,
          ...(filter.contractNumber && {
            contractNumber: filter.contractNumber,
          }),
        },
      });
    } else if (action.key === 'copy') {
      this.executeCopy(filter);
    } else if (action.key === 'rename') {
      this.renameDialog.show(filter.filterName);
    } else if (action.label === 'Share' && this.isUserFilter(filter)) {
      const userFilter = filter as UserFilter;
      this.shareDialog.show(
        'filter',
        userFilter.filterId,
        userFilter.contractNumber ? parseInt(userFilter.contractNumber) : 0,
        userFilter.filterName,
      );
    }
  }

  private async executeDelete(filter: SystemFilter | UserFilter) {
    const response = await this.filtersService.deleteFilter(
      filter.filterId,
      'JURIS',
    );

    const result = response.results[0];
    if (result.success) {
      this.snackbar.show(result.message, 'Success');
      delete this.filterDetails[filter.filterId];
    } else {
      //this part wont get hit as we disable the delete option but leaving it here in case a delete is attempted on a filter that is referenced by a report
      if (result.requestsUsingFilter && result.requestsUsingFilter.length > 0) {
        const requests = result.requestsUsingFilter.join(', ');
        this.snackbar.show(
          `${result.message} Used by: ${requests}`,
          'Error',
          'fa-light fa-circle-exclamation',
          10000,
        );
      } else {
        this.snackbar.show(result.message, 'Error');
      }
    }
  }

  private executeCopy(filter: SystemFilter | UserFilter) {
    this.router.navigate(['/reports/edit-filter', filter.filterId], {
      queryParams: {
        copy: true,
        isSystem: !this.isUserFilter(filter),
        redirectTo: this.redirectTo,
        ...(filter.contractNumber && { contractNumber: filter.contractNumber }),
      },
    });
  }

  navigateNewFilter() {
    // PrimeNG overlays appended to body are not cleaned up when projected components are destroyed during navigation
    document
      .querySelectorAll('.p-select-overlay, .p-multiselect-overlay')
      .forEach((el) => el.remove());
    const queryParams = this.showSearch
      ? { redirectTo: 'view?tab=filter' }
      : {};
    this.router.navigate(['/reports/edit-filter'], { queryParams });
  }

  showPreferences() {
    this.prefDialog.show();
  }

  async onRowExpand(event: { data: SystemFilter | UserFilter }) {
    const filter = event.data;
    if (!this.filterDetails[filter.filterId]) {
      await this.loadFilterDetail(
        filter.filterId,
        filter.contractNumber || null,
      );
      // Override filterName and description with values from the filter list
      this.filterDetails[filter.filterId].filterName = filter.filterName;
      if ('description' in filter) {
        this.filterDetails[filter.filterId].description = filter.description;
      }
    }
  }

  private async loadFilterDetail(filterId: string, contractNumber: string) {
    const filterDetail = await this.filtersService.getFilterDetail(
      filterId,
      contractNumber,
    );
    this.filterDetails[filterId] = filterDetail;
  }

  async renameFilter(newName: string, filter: SystemFilter | UserFilter) {
    this.renameDialog.setLoading(true);

    const response = await this.filtersService.renameFilter(
      filter.filterId,
      newName,
    );

    if (response.success) {
      this.snackbar.show(
        'Filter renamed successfully',
        'Success',
        'fa-light fa-check',
        3000,
      );
      this.renameDialog.close();
    } else {
      this.renameDialog.setLoading(false);
      this.snackbar.show(
        response.errorMessage || 'Failed to rename filter',
        'Error',
        'fa-light fa-exclamation-triangle',
        5000,
      );
    }
  }

  async refresh() {
    await this.filtersService.loadAllFilters('JURIS', 'CLAIM', null, true);
  }
}
